/**
 * Shared status semantics — used by dashboards, panels, and badges to
 * categorize statuses without each page repeating the same string sets.
 *
 * Stays in sync with `components/status-pill.tsx`; if a new status enters
 * the API, add it here AND in the pill's tone table.
 */

export const IN_PROGRESS_STATUSES = new Set<string>([
  'queued',
  'discovering',
  'crawling',
  'checking',
  'scoring',
  'narrating',
  'rendering',
  'pulling',
  'analyzing',
  'researching',
  'drafting',
  'delivering',
  'expanding',
  'enriching',
  'classifying',
  'clustering',
  'planning',
  'executing',
  'fitting',
  'projecting',
  // A5 v2 competitor content analysis
  'fetching',
  'extracting',
  'embedding',
  'synthesizing',
  'critiquing',
]);

export const FAILED_STATUSES = new Set<string>(['failed']);
export const NEEDS_REVIEW_STATUSES = new Set<string>(['needs_review', 'awaiting_approval']);
export const SUCCESS_STATUSES = new Set<string>([
  'completed',
  'delivered',
  'approved',
  'ready',
]);

export function isInProgress(status: string | null | undefined): boolean {
  return !!status && IN_PROGRESS_STATUSES.has(status);
}
export function isFailed(status: string | null | undefined): boolean {
  return !!status && FAILED_STATUSES.has(status);
}
export function isNeedsReview(status: string | null | undefined): boolean {
  return !!status && NEEDS_REVIEW_STATUSES.has(status);
}
export function isSuccess(status: string | null | undefined): boolean {
  return !!status && SUCCESS_STATUSES.has(status);
}
export function isTerminal(status: string | null | undefined): boolean {
  return isSuccess(status) || isFailed(status) || status === 'cancelled';
}

export interface StatusCounts {
  total: number;
  inProgress: number;
  failed: number;
  needsReview: number;
  succeeded: number;
}

/** Bucket an array of items by status into counts. */
export function countByStatus<T extends { status: string }>(
  items: readonly T[],
): StatusCounts {
  let inProgress = 0;
  let failed = 0;
  let needsReview = 0;
  let succeeded = 0;
  for (const it of items) {
    if (isInProgress(it.status)) inProgress++;
    else if (isFailed(it.status)) failed++;
    else if (isNeedsReview(it.status)) needsReview++;
    else if (isSuccess(it.status)) succeeded++;
  }
  return { total: items.length, inProgress, failed, needsReview, succeeded };
}
